/**
 * 시스템 관리 → [데이터 관리] 탭 (Phase 8-1) — admin only.
 *
 * 운영 엑셀 4종 업로드 → 운영 데이터 삭제 후 재적재. (2026-07-19 주석 갱신: 비상벨 슬롯 포함 4종)
 *
 * 기능:
 *   1. 현재 적재 카운트 요약 (실시간)
 *   2. 파일 4개 업로드 슬롯 (영업진행 / 매입대장 / 유지보수 / 비상벨)
 *   3. 적재 직전 확인 다이얼로그
 *   4. 적재 결과 리포트 (카운트 + 이슈 목록)
 *
 * 적재되는 데이터:
 *   - 영업진행 → sales 테이블 (지정 시)
 *   - 매입대장 → item_master + purchase + 주요재고 표시
 *   - 유지보수 → maintenance_contract + maintenance_sales (월별 분개)
 *
 * 보존되는 데이터:
 *   - 사용자 계정 / OTP / 신뢰 디바이스 / 변경이력 / SM·EJ 회사
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as adminApi from '../lib/admin-api';
import type { CalendarCleanupResult } from '../lib/admin-api';
import type {
  DataReloadReport,
  DataStatusCounts,
  DataYearsCategory,
  DataDeleteYearReport,
} from '../lib/types';
import { LoadingLines } from './ui/Skeleton';

type SlotKey = 'sales' | 'purchase' | 'maintenance' | 'bell';

const FILE_SLOTS: { key: SlotKey; label: string; hint: string }[] = [
  { key: 'sales',       label: '영업진행 데이터',     hint: '예: "영업진행 데이터.xlsx" (시트 2026/2025)' },
  { key: 'purchase',    label: '매입관리대장',         hint: '예: "2026년 매입관리대장 샘플.xlsm" (시트 매입관리대장 / 품명&품목 / 재고현황_주요품목)' },
  { key: 'maintenance', label: '유지보수 리스트',       hint: '예: "26년_유지보수 리스트_v1.0.xlsx" (시트 2026년)' },
  { key: 'bell',        label: '비상벨 자료',           hint: '예: "현진ICT 비상벨 자료.xlsx" (운영 엑셀 18컬럼)' },
];

const YEAR_CATEGORIES: { key: DataYearsCategory; label: string; descr: string }[] = [
  { key: 'sales',       label: '영업진행',       descr: '매출일련번호 앞 4자리 기준. 매입 행은 sales_id 가 NULL 로 끊기고 보존됩니다.' },
  { key: 'purchase',    label: '매입관리',       descr: '입고일(actual_in_date) 의 연도 기준. 매입 행만 삭제.' },
  { key: 'maintenance', label: '유지보수 매출',  descr: '월별 매출(revenue_year) 기준. 계약 마스터는 보존됩니다.' },
  { key: 'bell',        label: '비상벨',          descr: '계약일(contract_date) 의 연도 기준. 단축등록 자유입력 행(계약일 없음) 은 제외.' },
];

export function DataManagementTab() {
  const qc = useQueryClient();
  const [files, setFiles] = useState<{ sales?: File; purchase?: File; maintenance?: File; bell?: File }>({});
  const [report, setReport] = useState<DataReloadReport | null>(null);
  const [showAllIssues, setShowAllIssues] = useState(false);
  const [deleteReport, setDeleteReport] = useState<DataDeleteYearReport | null>(null);

  const statusQ = useQuery({
    queryKey: ['admin', 'data', 'status'],
    queryFn: () => adminApi.getDataStatus(),
    staleTime: 0,
  });

  const yearsQ = useQuery({
    queryKey: ['admin', 'data', 'years'],
    queryFn: () => adminApi.getDataYears(),
    staleTime: 0,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'data', 'status'] });
    qc.invalidateQueries({ queryKey: ['admin', 'data', 'years'] });
    qc.invalidateQueries({ queryKey: ['data-info'] });
    // 데이터 의존 쿼리 전체 invalidate
    qc.invalidateQueries({ queryKey: ['sales'] });
    qc.invalidateQueries({ queryKey: ['purchases'] });
    qc.invalidateQueries({ queryKey: ['stock'] });
    qc.invalidateQueries({ queryKey: ['analysis'] });
    qc.invalidateQueries({ queryKey: ['maintenance'] });
    qc.invalidateQueries({ queryKey: ['bell'] });
  };

  const reloadMut = useMutation({
    mutationFn: (input: typeof files) => adminApi.reloadData(input),
    onSuccess: (res) => {
      setReport(res.report);
      setShowAllIssues(false);
      invalidateAll();
    },
    onError: (e: Error) => {
      alert(`재적재 실패: ${e.message}`);
    },
  });

  const deleteYearMut = useMutation({
    mutationFn: (input: { category: DataYearsCategory; years: number[] }) =>
      adminApi.deleteDataByYear(input.category, input.years),
    onSuccess: (res) => {
      setDeleteReport(res);
      invalidateAll();
    },
    onError: (e: Error) => {
      alert(`연도별 삭제 실패: ${e.message}`);
    },
  });

  const counts: DataStatusCounts | undefined = statusQ.data?.counts;
  const totalRows = counts
    ? counts.employee + counts.sales + counts.purchase + counts.item_master +
      counts.maintenance_contract + counts.maintenance_sales + counts.emergency_bell_sales
    : 0;
  const hasAnyFile = !!files.sales || !!files.purchase || !!files.maintenance || !!files.bell;
  const isReloading = reloadMut.isPending;

  function pickFile(slot: SlotKey, f: File | null) {
    setFiles((cur) => ({ ...cur, [slot]: f ?? undefined }));
    setReport(null);
  }

  function handleReload() {
    if (!hasAnyFile) {
      alert('파일을 1개 이상 선택하세요.');
      return;
    }
    const fileList = Object.entries(files)
      .filter(([, f]) => !!f)
      .map(([k, f]) => `  • ${slotLabel(k)} : ${(f as File).name}`)
      .join('\n');
    const msg =
      `다음 파일로 운영 데이터를 재적재합니다:\n${fileList}\n\n` +
      `현재 데이터 (영업 ${counts?.sales ?? '-'}건 / 매입 ${counts?.purchase ?? '-'}건 / ` +
      `유지보수 ${counts?.maintenance_sales ?? '-'}건 / 비상벨 ${counts?.emergency_bell_sales ?? '-'}건 등)` +
      `가 모두 삭제되고 새로 적재됩니다.\n\n` +
      `사용자 계정·OTP·신뢰 디바이스·변경이력은 보존됩니다.\n\n계속하시겠습니까?`;
    if (!window.confirm(msg)) return;
    reloadMut.mutate(files);
  }

  return (
    <>
      {/* 현재 적재 상태 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4 mb-5">
        <div className="flex justify-between items-center mb-3">
          <h2 className="ui-section-title">현재 적재 상태</h2>
          <button
            type="button"
            onClick={() => qc.invalidateQueries({ queryKey: ['admin', 'data', 'status'] })}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            ↻ 새로고침
          </button>
        </div>
        {statusQ.isLoading && <div className="text-sm text-slate-400"><LoadingLines /></div>}
        {counts && (
          <div className="grid grid-cols-8 gap-2 text-sm">
            <Stat label="직원"        v={counts.employee} />
            <Stat label="품목"        v={counts.item_master} hint={`주요 ${counts.key_stock}`} />
            <Stat label="영업"        v={counts.sales} />
            <Stat label="매입"        v={counts.purchase} />
            <Stat label="유지보수계약" v={counts.maintenance_contract} />
            <Stat label="유지보수발행" v={counts.maintenance_sales} />
            <Stat label="비상벨"       v={counts.emergency_bell_sales} />
            <Stat label="총행수"       v={totalRows} highlight />
          </div>
        )}
      </div>

      {/* 파일 업로드 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4 mb-5">
        <h2 className="ui-section-title mb-2">엑셀 파일 선택</h2>
        <p className="text-xs text-slate-500 mb-3">
          4개 파일 중 일부만 적재해도 됩니다 (선택한 카테고리만 삭제 후 재적재).<br />
          단, <strong>영업진행 파일을 같이 적재해야 직원 마스터가 갱신</strong>됩니다 — 유지보수 단독 적재 시
          담당자(emp_id) 기본값이 채워지지 않을 수 있습니다.
        </p>
        <div className="space-y-3">
          {FILE_SLOTS.map((slot) => (
            <div key={slot.key} className="flex items-center gap-3 p-2 border border-slate-200 rounded">
              <label className="flex-1">
                <div className="text-sm font-medium text-slate-800">{slot.label}</div>
                <div className="text-xs text-slate-500 mb-1">{slot.hint}</div>
                <input
                  type="file"
                  accept=".xlsx,.xlsm"
                  onChange={(e) => pickFile(slot.key, e.target.files?.[0] ?? null)}
                  className="text-xs"
                />
              </label>
              {files[slot.key] && (
                <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                  ✓ {(files[slot.key] as File).name} ({fmtSize((files[slot.key] as File).size)})
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            ⚠ <strong>운영 데이터가 모두 삭제</strong>됩니다. 사용자 계정·OTP·디바이스·변경이력은 보존.
          </div>
          <button
            type="button"
            onClick={handleReload}
            disabled={!hasAnyFile || isReloading}
            className={`btn ${!hasAnyFile || isReloading ? 'btn-secondary' : 'btn-danger-solid'}`}
          >
            {isReloading ? '적재 중… (최대 1분)' : '🗑 삭제 후 재적재'}
          </button>
        </div>
      </div>

      {/* 적재 결과 */}
      {report && <ReportPanel report={report} showAll={showAllIssues} setShowAll={setShowAllIssues} />}

      {/* ── 연도별 삭제 ── */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="ui-section-title">연도별 데이터 삭제</h2>
          <button
            type="button"
            onClick={() => qc.invalidateQueries({ queryKey: ['admin', 'data', 'years'] })}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            ↻ 새로고침
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          잘못 적재된 연도만 골라 삭제. 4개 카테고리별로 데이터 있는 연도가 체크박스로 표시됩니다.
          삭제 후엔 운영 엑셀 재업로드로 다시 채울 수 있습니다.
        </p>
        {yearsQ.isLoading && <div className="text-sm text-slate-400"><LoadingLines /></div>}
        {yearsQ.data && (
          <div className="grid grid-cols-2 gap-3">
            {YEAR_CATEGORIES.map((cat) => (
              <YearCategoryCard
                key={cat.key}
                cat={cat}
                years={yearsQ.data![cat.key]}
                onDelete={(years) => {
                  setDeleteReport(null);
                  deleteYearMut.mutate({ category: cat.key, years });
                }}
                deleting={deleteYearMut.isPending}
              />
            ))}
          </div>
        )}
        {deleteReport && (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
            <div className="font-semibold text-emerald-800 mb-1">
              ✓ {YEAR_CATEGORIES.find((c) => c.key === deleteReport.category)?.label}{' '}
              {deleteReport.years.join(', ')}년 삭제 완료
            </div>
            <div className="text-xs text-slate-700">
              {Object.entries(deleteReport.deleted).map(([k, v]) => (
                <span key={k} className="mr-3">
                  {k}: <strong>{v.toLocaleString()}</strong>건
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

    </>
  );
}

// 포털이 네이버웍스에 자동 등록한 '납품/납품예정' 일정 중, 지정 날짜 이전(납품예정일 기준)을 일괄 삭제.
//   수기로 미리 등록해 둔 일정과 중복될 때 사용. 미리보기(대상 확인) → 삭제 2단계.
//   2026-07-19 메뉴 재구성: 데이터 관리 탭 하단에 매몰돼 있던 것을 시스템 관리 [연동 도구] 탭으로 승격 (export).
export function CalendarCleanupCard() {
  const [before, setBefore] = useState('2026-06-01');
  const [preview, setPreview] = useState<CalendarCleanupResult | null>(null);
  const [result, setResult] = useState<CalendarCleanupResult | null>(null);

  const previewMut = useMutation({
    mutationFn: () => adminApi.cleanupPortalCalendarEvents(before, true),
    onSuccess: (r) => { setPreview(r); setResult(null); },
    onError: (e: Error) => alert(`미리보기 실패: ${e.message}`),
  });
  const deleteMut = useMutation({
    mutationFn: () => adminApi.cleanupPortalCalendarEvents(before, false),
    onSuccess: (r) => { setResult(r); setPreview(null); },
    onError: (e: Error) => alert(`삭제 실패: ${e.message}`),
  });

  function handleDelete() {
    const cnt = preview?.count ?? 0;
    if (cnt === 0) return;
    if (!window.confirm(
      `${before} 이전 '납품/납품예정' 중 포털이 자동 등록한 캘린더 일정 ${cnt}건을 네이버웍스에서 삭제합니다.\n\n` +
      `수기로 등록한 일정은 영향받지 않습니다.\n삭제 후 해당 영업건을 다시 저장하면 재등록될 수 있습니다.\n\n계속할까요?`,
    )) return;
    deleteMut.mutate();
  }

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-4 mt-6">
      <h2 className="ui-section-title mb-1">📅 포털 캘린더 중복 정리 (네이버웍스)</h2>
      <p className="text-xs text-slate-500 mb-3">
        지정한 날짜 이전(납품예정일 기준)에 <strong>포털이 자동 등록한</strong> 네이버웍스 일정을 삭제합니다.
        수기로 미리 등록한 일정과 중복될 때 사용하세요 — <strong>수기 일정은 영향 없음</strong>.
      </p>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <label className="ui-label">이 날짜 이전:</label>
        <input
          type="date"
          value={before}
          onChange={(e) => { setBefore(e.target.value); setPreview(null); setResult(null); }}
          className="ui-input ui-input-sm"
        />
        <button
          type="button"
          onClick={() => previewMut.mutate()}
          disabled={previewMut.isPending || !before}
          className="btn btn-secondary"
        >
          {previewMut.isPending ? '확인 중…' : '미리보기'}
        </button>
      </div>

      {preview && (
        <div className="border border-slate-200 rounded p-3 mb-3">
          <div className="text-sm font-medium mb-2">삭제 대상 {preview.count ?? 0}건</div>
          {(preview.count ?? 0) === 0 ? (
            <div className="text-xs text-slate-500">대상 없음 — 삭제할 포털 등록 일정이 없습니다.</div>
          ) : (
            <>
              <div className="max-h-60 overflow-y-auto border border-slate-100 rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1">영업번호</th>
                      <th className="text-left px-2 py-1">예정일</th>
                      <th className="text-left px-2 py-1">상태</th>
                      <th className="text-left px-2 py-1">엔드유저</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.targets?.map((t) => (
                      <tr key={t.sales_id} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-mono">{t.sales_id}</td>
                        <td className="px-2 py-1">{t.exp_date}</td>
                        <td className="px-2 py-1">{t.status ?? '-'}</td>
                        <td className="px-2 py-1">{t.end_user ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMut.isPending}
                className="btn btn-danger-solid mt-3"
              >
                {deleteMut.isPending ? '삭제 중…' : `🗑 ${preview.count}건 삭제`}
              </button>
            </>
          )}
        </div>
      )}

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
          <div className="font-semibold text-emerald-800">
            ✓ 삭제 완료 — 성공 {result.deleted ?? 0}건{result.failed ? `, 실패 ${result.failed}건` : ''}
          </div>
          {result.results?.filter((r) => !r.ok).map((r) => (
            <div key={r.salesId} className="text-xs text-red-600 mt-1">실패: {r.salesId} — {r.error}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function YearCategoryCard({
  cat,
  years,
  onDelete,
  deleting,
}: {
  cat: { key: DataYearsCategory; label: string; descr: string };
  years: { year: number; count: number }[];
  onDelete: (years: number[]) => void;
  deleting: boolean;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set());

  function toggle(y: number) {
    const next = new Set(selected);
    if (next.has(y)) next.delete(y);
    else next.add(y);
    setSelected(next);
  }

  function handleClick() {
    if (selected.size === 0) return;
    const total = years
      .filter((y) => selected.has(y.year))
      .reduce((a, r) => a + r.count, 0);
    const yearList = [...selected].sort().join(', ');
    if (!window.confirm(
      `[${cat.label}] ${yearList}년 데이터를 삭제할까요?\n\n` +
      `대상 행수: ${total.toLocaleString()}건\n\n` +
      `${cat.descr}\n\n` +
      `삭제 후 복구할 수 없습니다 (변경 이력엔 기록됨).`,
    )) return;
    onDelete([...selected]);
    setSelected(new Set());
  }

  return (
    <div className="border border-slate-200 rounded p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-slate-800">{cat.label}</div>
        <button
          type="button"
          onClick={handleClick}
          disabled={selected.size === 0 || deleting}
          className={`btn btn-sm ${selected.size === 0 || deleting ? 'btn-secondary' : 'btn-danger-solid'}`}
        >
          {deleting ? '삭제 중…' : `🗑 ${selected.size > 0 ? `${selected.size}개 연도 ` : ''}삭제`}
        </button>
      </div>
      <div className="text-xs text-slate-500 mb-2">{cat.descr}</div>
      {years.length === 0 ? (
        <div className="text-xs text-slate-500 py-2">적재된 데이터 없음</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {years.map((y) => (
            <label
              key={y.year}
              className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer border transition ${
                selected.has(y.year)
                  ? 'bg-red-50 border-red-300 text-red-800'
                  : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
              }`}
            >
              <input
                type="checkbox"
                checked={selected.has(y.year)}
                onChange={() => toggle(y.year)}
                disabled={deleting}
              />
              <span className="font-semibold">{y.year}</span>
              <span className="text-slate-500">({y.count.toLocaleString()})</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function slotLabel(k: string): string {
  if (k === 'sales') return '영업진행 데이터';
  if (k === 'purchase') return '매입관리대장';
  if (k === 'maintenance') return '유지보수 리스트';
  if (k === 'bell') return '비상벨 자료';
  return k;
}

function fmtSize(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(2)}MB`;
}

function Stat({ label, v, highlight, hint }: { label: string; v: number; highlight?: boolean; hint?: string }) {
  return (
    <div
      className={`text-center py-2 rounded ${
        highlight ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-slate-200'
      }`}
    >
      <div className="text-xs text-slate-500 mb-0.5">{label}</div>
      <div className={`font-semibold ${highlight ? 'text-blue-700' : 'text-slate-900'}`}>
        {v.toLocaleString()}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function ReportPanel({
  report, showAll, setShowAll,
}: { report: DataReloadReport; showAll: boolean; setShowAll: (b: boolean) => void }) {
  const errors = report.issues.filter((i) => i.level === 'error');
  const warns = report.issues.filter((i) => i.level === 'warn');
  const visible = showAll ? report.issues : report.issues.slice(0, 30);

  const before = report.deletedBefore;
  const after = report.counts;

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-4">
      <h2 className="ui-section-title mb-3">
        {report.ok ? '✅ 적재 완료' : '⚠️ 적재 완료 (오류 있음)'}
        <span className="ml-2 text-xs text-slate-500">
          {(report.durationMs / 1000).toFixed(2)}초 · 오류 {errors.length}건 · 경고 {warns.length}건
        </span>
      </h2>

      {/* 카운트 비교 */}
      <table className="data-table w-full text-[13px] mb-4">
        <thead>
          <tr>
            <th className="text-left">테이블</th>
            <th className="text-right w-32">삭제 전</th>
            <th className="text-right w-32">적재 후</th>
            <th className="text-right w-32">증감</th>
          </tr>
        </thead>
        <tbody>
          <CountRow label="직원 마스터"          before={before.employee} after={after.employee} />
          <CountRow label="품목 마스터"          before={before.item_master} after={after.item_master} />
          <CountRow label="영업진행"             before={before.sales} after={after.sales} />
          <CountRow label="매입"                 before={before.purchase} after={after.purchase} />
          <CountRow label="유지보수 계약"        before={before.maintenance_contract} after={after.maintenance_contract} />
          <CountRow label="유지보수 매출(월별)"   before={before.maintenance_sales} after={after.maintenance_sales} />
          <CountRow label="비상벨"                 before={before.emergency_bell_sales} after={after.emergency_bell_sales} />
        </tbody>
      </table>

      {after.item_master_key_stock > 0 && (
        <div className="text-xs text-slate-600 mb-3">
          📦 주요재고 표시 적용: <strong>{after.item_master_key_stock}건</strong>
        </div>
      )}

      {/* 이슈 목록 */}
      {report.issues.length > 0 && (
        <div>
          <h3 className="ui-subsection-title mb-2">이슈 목록</h3>
          <div className="bg-slate-50 border border-slate-200 rounded p-2 max-h-96 overflow-y-auto text-xs font-mono">
            {visible.map((it, idx) => (
              <div key={idx} className={it.level === 'error' ? 'text-red-700' : 'text-amber-700'}>
                {it.level === 'error' ? '❌' : '⚠️'} [{it.source}] {it.message}
              </div>
            ))}
          </div>
          {report.issues.length > 30 && (
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="text-xs text-blue-600 hover:underline mt-2"
            >
              {showAll ? '처음 30건만 보기' : `전체 ${report.issues.length}건 보기`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CountRow({ label, before, after }: { label: string; before: number | undefined; after: number }) {
  const b = before ?? 0;
  const delta = after - b;
  return (
    <tr>
      <td>{label}</td>
      <td className="text-right text-slate-500">{b.toLocaleString()}</td>
      <td className="text-right font-semibold">{after.toLocaleString()}</td>
      <td className={`text-right ${delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-red-600' : 'text-slate-400'}`}>
        {delta > 0 ? '+' : ''}{delta.toLocaleString()}
      </td>
    </tr>
  );
}
